import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AbsenceDetailModal } from '@/components/Dashboard/AbsenceDetailModal';
import type { AbsenceDetailSelection } from '@/components/Dashboard/AbsencesModal';
import { EmptyTileState } from '@/components/Dashboard/EmptyTileState';
import { HeaderCard } from '@/components/Cards/HeaderCard';
import { Button } from '@/components/Button';
import { ChevronRight } from '@/components/Icons/ChevronRight';
import { PlusSign } from '@/components/Icons/PlusSign';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import { Icon } from '@/components/Icons';
import { Tooltip } from '@/components/Tooltip';
import { AbsenceActiveCard } from '@/pages/Overview/AbsenceActiveCard';
import { useAttendance } from '@/pages/Overview/hooks/useAttendance';
import { useSelectedPeriod } from '@/pages/Overview/hooks/useSelectedPeriod';
import { JustificationsCard } from '@/pages/Overview/JustificationsCard';
import { PeriodPicker } from '@/pages/Overview/PeriodPicker';
import { DisciplineTypeCard } from '@/pages/Overview/DisciplineTypeCard';
import { MandatoryContentCard } from '@/pages/Overview/MandatoryContentCard';
import { ExcuseCard } from '@/pages/Overview/ExcuseCard';
import {
  ButtonVariant,
  HeaderCardIcon,
  IconType,
  Size,
} from '@/types/components';
import type { NonAttendance } from '@/types/overview';

const gridColumnsByCardCount: Record<number, string> = {
  1: '',
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
};

export const Attendance: React.FC = () => {
  const { t } = useTranslation();
  const { periods, selectedPeriod, selectPeriod } = useSelectedPeriod();
  const [selectedAbsence, setSelectedAbsence] = useState<
    AbsenceDetailSelection | undefined
  >();
  const [isExcuseTooltipOpen, setIsExcuseTooltipOpen] = useState(false);
  const {
    isLoading,
    canReportAbsence,
    activeApplications,
    statistics,
    mandatoryContent,
    attendedDisciplineTypes,
    excuses,
    openAbsenceApplication,
    openAbsenceHistory,
    onOpenDetail,
  } = useAttendance(selectedPeriod);

  const hasExcuses = !!excuses && excuses.length > 0;
  const hasDisciplineTypeCards = attendedDisciplineTypes.length > 0;

  const handleAbsenceClick = (absence: NonAttendance, date: string) =>
    setSelectedAbsence({ absence, date });

  return (
    <div className="flex flex-1 flex-col pt-1 pb-4 md:flex-none md:px-6 md:py-6">
      <div className="flex flex-1 flex-col gap-6 pb-8 md:min-h-[460px] md:flex-none md:gap-10 md:rounded-[20px] md:bg-card md:pt-6">
        {canReportAbsence && (
          <div className="flex px-5 md:hidden">
            <HeaderCard
              label={t('global.reportAbsence')}
              icon={HeaderCardIcon.Absence}
              bgClassName="bg-warning-surface"
              onClick={openAbsenceApplication}
            />
          </div>
        )}

        <section className="hidden flex-wrap items-start justify-between gap-4 px-5 md:flex">
          <h1 className="text-title-24 text-foreground">
            {t('overview.tabs.attendance')}
          </h1>
          <div className="flex flex-col items-stretch gap-3 min-[360px]:flex-row min-[360px]:items-center md:w-auto">
            {canReportAbsence && (
              <Button
                variant={ButtonVariant.Primary}
                size={Size.Medium}
                className="min-w-0 gap-2 pr-4 pl-3 text-subhead-14 font-semibold min-[360px]:flex-1 md:flex-none"
                onClick={openAbsenceApplication}
              >
                <PlusSign strokeWidth="1.8" className="size-4 shrink-0" />
                <span className="truncate">{t('global.reportAbsence')}</span>
              </Button>
            )}
            {canReportAbsence && (
              <Button
                variant={ButtonVariant.Secondary}
                size={Size.Medium}
                className="min-w-0 gap-2 pr-2 pl-4 text-subhead-14 font-semibold min-[360px]:flex-1 md:flex-none"
                onClick={openAbsenceHistory}
              >
                <span className="truncate">
                  {t('absenceApplication.history')}
                </span>
                <ChevronRight className="size-4 shrink-0" strokeWidth="1.8" />
              </Button>
            )}
          </div>
        </section>

        {isLoading && (
          <div className="flex min-h-[50svh] flex-1 items-center justify-center md:min-h-0">
            <PageSpinner className="size-[20px] text-subtle-foreground" />
          </div>
        )}

        {!isLoading && statistics && (
          <>
            {canReportAbsence && (
              <section className="flex flex-col gap-4 md:hidden">
                <div className="flex items-center justify-between gap-3 px-5">
                  <h2 className="text-callout-16 text-foreground">
                    {t('overview.attendance.active')}
                  </h2>
                  <button
                    type="button"
                    onClick={openAbsenceHistory}
                    className="text-subhead-14 flex shrink-0 cursor-pointer items-center gap-1 text-primary"
                  >
                    {t('absenceHistory.all')}
                    <ChevronRight
                      className="size-4 shrink-0"
                      strokeWidth="1.6"
                    />
                  </button>
                </div>
                {activeApplications.length === 0 && (
                  <div className="px-5">
                    <EmptyTileState />
                  </div>
                )}
                {activeApplications.length > 0 && (
                  <div className="grid grid-cols-1 gap-2 px-5">
                    {activeApplications.map((application) => (
                      <AbsenceActiveCard
                        key={application.id}
                        application={application}
                        onClick={onOpenDetail}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}

            {activeApplications.length > 0 && (
              <section className="hidden flex-col gap-4 md:flex">
                <h2 className="text-callout-16 px-5 text-foreground">
                  {t('overview.attendance.active')}
                </h2>
                <div className="grid grid-cols-2 gap-4 px-5 xl:grid-cols-3">
                  {activeApplications.map((application) => (
                    <AbsenceActiveCard
                      key={application.id}
                      application={application}
                      onClick={onOpenDetail}
                    />
                  ))}
                </div>
              </section>
            )}

            <section className="flex flex-col gap-3 px-5 md:gap-4">
              <div className="flex items-center gap-3">
                <h2 className="text-callout-16 text-foreground">
                  {t('overview.attendance.statistics')}
                </h2>
                <PeriodPicker
                  periods={periods}
                  value={selectedPeriod}
                  onChange={selectPeriod}
                />
              </div>
              <div className="flex flex-col gap-2 md:gap-3">
                <MandatoryContentCard
                  statistics={mandatoryContent}
                  hasTitle={hasDisciplineTypeCards}
                />
                {hasDisciplineTypeCards && (
                  <div
                    className={`grid gap-2 md:gap-4 ${gridColumnsByCardCount[attendedDisciplineTypes.length]}`}
                  >
                    {attendedDisciplineTypes.map((discipline) => (
                      <DisciplineTypeCard
                        key={discipline.type}
                        statistics={discipline}
                      />
                    ))}
                  </div>
                )}
                <div className="flex flex-col items-start gap-2 md:flex-row md:gap-3">
                  <JustificationsCard
                    title={t('overview.attendance.justified')}
                    entries={statistics.justified}
                    onAbsenceClick={handleAbsenceClick}
                  />
                  <JustificationsCard
                    title={t('overview.attendance.nonJustified')}
                    entries={statistics.nonJustified}
                    onAbsenceClick={handleAbsenceClick}
                  />
                </div>
              </div>
            </section>

            <section className="flex flex-col gap-4 px-5">
              <div className="flex items-center gap-2">
                <h2 className="text-callout-16 text-foreground">
                  {t('overview.attendance.notes')}
                </h2>
                <Tooltip
                  content={t('overview.attendance.notesTooltip')}
                  side="right"
                  isOpen={isExcuseTooltipOpen}
                  onOpenChange={setIsExcuseTooltipOpen}
                >
                  <button
                    type="button"
                    data-tooltip-trigger
                    onClick={() => setIsExcuseTooltipOpen((prev) => !prev)}
                    className="flex size-4 cursor-pointer items-center justify-center text-subtle-foreground"
                  >
                    <Icon icon={IconType.Support} className="size-4" />
                  </button>
                </Tooltip>
              </div>
              {!hasExcuses && <EmptyTileState />}
              {hasExcuses && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {excuses.map((excuse, index) => (
                    <ExcuseCard key={index} excuse={excuse} />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
      <AbsenceDetailModal
        selection={selectedAbsence}
        onClose={() => setSelectedAbsence(undefined)}
      />
    </div>
  );
};
